import { Controller } from "@hotwired/stimulus"

// Manages theme switching between dark and light modes
// Usage: 
//   data-controller="theme" on body/html
//   data-action="click->theme#toggle" on toggle button
//   data-theme-target="icon" on icon element (optional)
export default class extends Controller {
  static targets = ["icon", "darkRadio", "lightRadio"]
  static values = {
    current: { type: String, default: "dark" }
  }

  connect() {
    // Load theme from localStorage first (for immediate application)
    const savedTheme = localStorage.getItem("theme") || "dark"
    this.applyTheme(savedTheme, false)
  }

  // Called when a theme radio button is selected in the profile form
  select(event) {
    const theme = event.target.value
    this.applyTheme(theme, true)
  }

  // Toggle between dark and light
  toggle() {
    const newTheme = this.currentValue === "dark" ? "light" : "dark"
    this.applyTheme(newTheme, true)
  }

  // Apply the theme to the page
  applyTheme(theme, animate = true) {
    this.currentValue = theme

    // Add transition class for smooth change
    if (animate) {
      document.documentElement.classList.add("theme-transitioning")
      setTimeout(() => {
        document.documentElement.classList.remove("theme-transitioning")
      }, 300)
    }

    // Apply theme attribute
    document.documentElement.dataset.theme = theme
    document.documentElement.setAttribute("data-bs-theme", theme)
    document.body.dataset.theme = theme

    // Update any icon targets
    this.updateIcons()

    // Update radio buttons if they exist
    this.updateRadios()

    // Update theme card visual states
    this.updateThemeCards()

    // Save preference to localStorage
    localStorage.setItem("theme", theme)

    // Sync to server if logged in (via API)
    this.syncToServer(theme)
  }

  updateIcons() {
    if (this.hasIconTarget) {
      const isDark = this.currentValue === "dark"
      this.iconTargets.forEach(icon => {
        // Support Bootstrap Icons (bi)
        if (icon.classList.contains("bi")) {
          icon.className = isDark ? "bi bi-moon-stars-fill" : "bi bi-sun-fill"
          // Preserve other classes if needed, but for now simple replacement is safer
          // If we need to preserve layout classes (e.g. me-2), we'd need more complex logic
          // But the footer icon is just <i class="bi bi-moon-stars-fill" ...>
        } 
        // Support FontAwesome (fas)
        else if (icon.classList.contains("fas")) {
          icon.className = isDark ? "fas fa-moon" : "fas fa-sun"
        } else {
          icon.textContent = isDark ? "🌙" : "☀️"
        }
      })
    }
  }

  updateRadios() {
    if (this.hasDarkRadioTarget) {
      this.darkRadioTarget.checked = this.currentValue === "dark"
    }
    if (this.hasLightRadioTarget) {
      this.lightRadioTarget.checked = this.currentValue === "light"
    }
  }

  updateThemeCards() {
    // Update theme card active states (for profile page)
    document.querySelectorAll('[data-theme-card]').forEach(card => {
      const cardTheme = card.dataset.themeCard
      if (cardTheme === this.currentValue) {
        card.classList.add('active')
      } else {
        card.classList.remove('active')
      }
    })
  }

  async syncToServer(theme) {
    // Only sync if we have a CSRF token (user is on a Rails page)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) return

    try {
      await fetch("/api/v1/preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ key: "theme", value: theme })
      })
    } catch (error) {
      // Fail silently - localStorage is the fallback
      console.debug("Could not sync theme preference to server:", error)
    }
  }
}
